import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { DefaultChatTransport } from 'ai'
import type { AgentKey, ChatTransport, Thread } from 'tailwind-v4/blocks/chat'
import type { ChatMessage, ChatMessagePart } from 'tailwind-v4/blocks/agent-chat'
import type { AppDispatch } from '@/state/store'
import trunkApi from '@/state/trunk/trunk.slice'
import type { TrunkMessage, TrunkMessageContentPart, TrunkThread } from '@/state/trunk/trunk.types'
import { getToken } from '@/utils/AuthTokenHelper'
import config from '@/config'

/**
 * A `ChatTransport` for any trunk agent served by the durable routes
 * (`/agents/<name>/{send,observe,active-run,abort}`), driven by
 * `useDurableChatThreads`.
 *
 * Thread CRUD reuses the GENERIC per-organisation thread endpoints (the same
 * ones the marketing chat widget uses). Those do not scope by agent, so every
 * thread is tagged `metadata.agentKey` on create and filtered back out on
 * list, keeping one agent's threads from mixing with another's in the same
 * organisation. `extraMetadata` lets a host tag threads further (the
 * enquiries panel records the enquiry a conversation was opened on).
 *
 * `streamTransport` is present only to satisfy the type: the durable hook
 * streams through the routes itself and never calls it.
 */
export interface TrunkAgentTransportOptions {
  agentKey: AgentKey
  /** Durable route base, e.g. '/agents/coordinator'. */
  basePath: string
  /** Extra `metadata` for threads created through this transport. */
  extraMetadata?: () => Record<string, unknown>
}

function mapThread(t: TrunkThread): Thread {
  return {
    id: t.id,
    title: t.title,
    preview: null,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
    metadata: (t.metadata as Record<string, unknown> | null) ?? undefined,
  }
}

const TOOL_STATE_MAP: Record<string, 'partial' | 'call' | 'result' | 'error'> = {
  'input-streaming': 'partial',
  'partial-call': 'partial',
  call: 'call',
  'output-available': 'result',
  result: 'result',
  complete: 'result',
}

/**
 * Map a stored message part to a ChatMessage part. Robust to the persisted
 * (Mastra v2) shapes so REPLAYED threads render tool calls the same as live.
 * What is actually persisted for a tool call is `{ type: 'tool-invocation',
 * toolInvocation: { toolName, toolCallId, args, state, result } }`, not the
 * `tool-{name}`-typed shape the live stream normalises to.
 */
export function mapTrunkPart(p: TrunkMessageContentPart, idHint: string): ChatMessagePart | null {
  const x = p as TrunkMessageContentPart & {
    toolName?: string
    args?: Record<string, unknown>
    result?: unknown
    errorText?: string
    input?: Record<string, unknown>
    output?: unknown
    toolCallId?: string
    state?: string
    toolInvocation?: {
      toolCallId?: string
      toolName?: string
      args?: Record<string, unknown>
      state?: string
      result?: unknown
    }
  }
  if (x.type === 'text' && typeof x.text === 'string') {
    return x.text ? { type: 'text', text: x.text } : null
  }
  if (x.type.startsWith('tool-') || x.type === 'dynamic-tool') {
    const inv = x.toolInvocation
    const name =
      x.toolName ??
      inv?.toolName ??
      (x.type.startsWith('tool-') && x.type !== 'tool-invocation' ? x.type.slice('tool-'.length) : 'tool')
    return {
      type: 'tool-call',
      toolCall: {
        id: x.toolCallId ?? inv?.toolCallId ?? `${idHint}-${name}`,
        name,
        args: x.input ?? x.args ?? inv?.args ?? {},
        state: TOOL_STATE_MAP[x.state ?? inv?.state ?? 'result'] ?? 'result',
        result: x.output ?? x.result ?? inv?.result,
        error: x.errorText,
      },
    }
  }
  return null
}

export function mapTrunkMessage(m: TrunkMessage): ChatMessage {
  let parts: ChatMessagePart[] = []
  if (typeof m.content === 'string') {
    if (m.content) parts = [{ type: 'text', text: m.content }]
  } else {
    const content = m.content
    parts = (content.parts ?? [])
      .map((p, i) => mapTrunkPart(p, `${m.id}-${i}`))
      .filter((p): p is ChatMessagePart => p !== null)
  }
  return {
    id: m.id,
    role: m.role,
    parts,
    createdAt: m.createdAt,
    status: 'complete',
  }
}

export function useTrunkAgentTransport(orgId: string, { agentKey, basePath, extraMetadata }: TrunkAgentTransportOptions): ChatTransport {
  const dispatch = useDispatch<AppDispatch>()
  const isOurs = (t: TrunkThread): boolean => (t.metadata as { agentKey?: string } | null)?.agentKey === agentKey

  return useMemo<ChatTransport>(
    () => ({
      streamTransport: new DefaultChatTransport({
        api: `${config.url.trunk}${basePath}/send`,
        headers: () => ({
          Authorization: `Bearer ${getToken()?.accessToken ?? ''}`,
        }),
      }),

      async listThreads() {
        const res = await dispatch(
          trunkApi.endpoints.listThreads.initiate({ orgId, limit: 100, offset: 0 }, { forceRefetch: true })
        ).unwrap()
        return res.threads.filter(isOurs).map(mapThread)
      },

      async getThread(id: string) {
        const [thread, msgRes] = await Promise.all([
          dispatch(trunkApi.endpoints.getThread.initiate({ orgId, threadId: id }, { forceRefetch: true })).unwrap(),
          dispatch(
            trunkApi.endpoints.listMessages.initiate({ orgId, threadId: id, limit: 1000 }, { forceRefetch: true })
          ).unwrap(),
        ])
        return {
          thread: mapThread(thread),
          messages: msgRes.messages.map(mapTrunkMessage),
        }
      },

      async createThread(input?: { title?: string; agentKey?: AgentKey }) {
        // POST /organisations/:orgId/threads returns the thread fields FLAT
        // (no `{ thread }` wrapper), whatever the RTK type claims; trace
        // trunk's routes/threads.ts before "fixing" this.
        const res = await dispatch(
          trunkApi.endpoints.createThread.initiate({
            orgId,
            data: {
              title: input?.title,
              metadata: { ...(extraMetadata?.() ?? {}), agentKey },
            },
          })
        ).unwrap()
        return mapThread(res as unknown as TrunkThread)
      },

      async deleteThread(id: string) {
        await dispatch(trunkApi.endpoints.deleteThread.initiate({ orgId, threadId: id })).unwrap()
      },

      async renameThread(id: string, title: string) {
        await dispatch(trunkApi.endpoints.updateThread.initiate({ orgId, threadId: id, data: { title } })).unwrap()
      },
    }),
    // extraMetadata is read at call time; a new function identity must not
    // rebuild the transport (which would reset the durable hook).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, orgId, agentKey, basePath]
  )
}
